import React, { useEffect, useRef } from "react";
import { Editor } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import "./style.css";

type CustomToolbarItem = {
  name: string;
  command: string;
  tooltip: string;
  className?: string;
  handler: (editor: any) => void;
};

interface Props {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onBlur: (markdown: string) => void;
  onChange?: (markdown: string) => void;
  customToolbarItems?: CustomToolbarItem[];
}

const toolbarItems = [
  ["heading", "bold", "italic"],
  ["ul", "ol", "indent", "outdent"],
  ["table", "link"],
];

const DEFAULT_HEIGHT = 132;

const MarkdownTextArea: React.FC<Props> = ({
  value,
  disabled,
  placeholder,
  onBlur,
  onChange,
  customToolbarItems = [],
}) => {
  const editorRef = useRef<Editor>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(DEFAULT_HEIGHT);
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) return;
    editorRef.current?.getInstance().setMarkdown(value || "");
  }, [value]);

  useEffect(() => {
    if (didInit.current && editorRef.current) {
      const instance = editorRef?.current?.getInstance();
      customToolbarItems.forEach((btn) => {
        instance?.addCommand("wysiwyg", btn.command, () => {
          btn.handler(instance);
          return true;
        });
      });
    }
  }, [didInit.current]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    wrapper.style.height = `${DEFAULT_HEIGHT}px`;
    lastHeightRef.current = DEFAULT_HEIGHT;

    // Custom styling logic to force outer wrapper of editor to be the same height as inner div that is resizable
    const wwContainer = wrapper.querySelector(
      ".toastui-editor-ww-container"
    ) as HTMLElement | null;

    if (!wwContainer) return;

    const observer = new ResizeObserver(() => {
      const rect = wwContainer.getBoundingClientRect();
      const contentHeight = Math.round(rect.height);

      if (contentHeight <= 0) return;

      const nextHeight = contentHeight + 32;

      if (Math.abs(nextHeight - lastHeightRef.current) < 1) return;

      lastHeightRef.current = nextHeight;
      wrapper.style.height = `${nextHeight}px`;
    });

    observer.observe(wwContainer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    //override native autofocus/scroll behaviors
    let lastScrollY = window.scrollY;

    const onScroll = () => {
      lastScrollY = window.scrollY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    const observer = new MutationObserver(() => {
      // restore scroll after any DOM/layout mutation
      requestAnimationFrame(() => {
        window.scrollTo({ top: lastScrollY, behavior: "auto" });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={wrapperRef} className="markdownTextArea">
      {/* @ts-ignore */}
      <Editor
        ref={editorRef}
        initialValue={value || ""}
        initialEditType="wysiwyg"
        hideModeSwitch
        usageStatistics={false}
        toolbarItems={[...toolbarItems, customToolbarItems]}
        height="100%"
        placeholder={placeholder}
        onBlur={() => {
          const markdown = editorRef.current?.getInstance()?.getMarkdown();
          if (markdown && markdown !== value) onBlur(markdown);
        }}
        onChange={() => {
          if (onChange) {
            const markdown = editorRef.current?.getInstance()?.getMarkdown();
            if (markdown && markdown !== value) onChange(markdown);
          }
        }}
        onLoad={() => {
          didInit.current = true;
        }}
        //@ts-ignore
        readOnly={disabled}
        autofocus={false}
      />
    </div>
  );
};

export default MarkdownTextArea;
